import React from "react";
import { Link } from "react-router-dom";
import { Lock, Phone, Heart } from "lucide-react";
import { BsnSeal } from "@/components/BsnSeal";
import { useEmergency } from "@/components/EmergencyProvider";
import { STORE_VISIBLE } from "@/lib/flags";
import { useLang } from "@/lib/lang";

const COLS = [
  {
    title: "Find Support", es: "Busca apoyo",
    links: [
      { to: "/resources", label: "Get Help / Resources", es: "Obtén ayuda / Recursos" },
      { to: "/stories", label: "Survivor Stories & Prayers", es: "Historias y oraciones de sobrevivientes" },
      { to: "/journal", label: "The Storyteller's Journal", es: "El diario del narrador" },
      { to: "/affirmations", label: "Rebuilding & Affirmations", es: "Reconstruir y afirmaciones" },
      { to: "/sanctuary", label: "Calming Media Sanctuary", es: "Santuario de calma" },
    ],
  },
  {
    title: "Community", es: "Comunidad",
    links: [
      { to: "/stay", label: "\u201cStay\u201d — New Song", es: "\u201cStay\u201d — Nueva canción" },
      { to: "/super-kids", label: "Super Kids Resources", es: "Recursos Super Kids" },
      { to: "/teen-facts", label: "Teens & Storm Watchers", es: "Jóvenes y Storm Watchers" },
      ...(STORE_VISIBLE ? [{ to: "/shop", label: "Digital Shop", es: "Tienda digital" }, { to: "/donations", label: "Donations & Patron Gifts", es: "Donaciones" }] : []),
      { to: "/training", label: "Training & Volunteering", es: "Capacitación y voluntariado" },
      { to: "/about", label: "About BSN", es: "Acerca de BSN" },
    ],
  },
  {
    title: "Connect", es: "Conecta",
    links: [
      { to: "/contact", label: "Contact Us", es: "Contáctanos" },
      { to: "/speaking", label: "Invite Mary to Speak", es: "Invita a Mary a hablar" },
      { to: "/legal", label: "Privacy & Terms", es: "Privacidad y términos" },
    ],
  },
];

export const Footer = () => {
  const { open } = useEmergency();
  const { lang } = useLang();
  const es = lang === "es";
  return (
    <footer data-i18n="manual" className="relative z-10 bg-bsn-midnight text-bsn-ice/80 border-t-2 border-bsn-gold/40" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <BsnSeal size={64} withText />
            <p className="mt-5 text-sm leading-relaxed text-bsn-ice/70 max-w-xs">
              {es ? "Reconstruyendo la esperanza, anclando el mañana. Un refugio para encontrar apoyo y redescubrir la luz dentro de tu propia historia." : "Rebuilding hope, anchoring tomorrow. A refined sanctuary for finding support and rediscovering the light within your own story."}
            </p>
            <button onClick={open} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full btn-gold transition-all" data-testid="footer-crisis-btn">
              <Phone className="w-4 h-4" /> {es ? "Apoyo en crisis 24/7" : "24/7 Crisis Support"}
            </button>
          </div>

          {COLS.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <h4 className="eyebrow text-bsn-gold mb-4">{es ? col.es : col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm hover:text-bsn-champagne transition-colors">{es ? l.es : l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {STORE_VISIBLE && <div className="md:col-span-2">
            <h4 className="eyebrow text-bsn-gold mb-4">{es ? "Dona" : "Give"}</h4>
            <Link to="/donations" className="inline-flex items-center gap-2 text-sm font-semibold hover:text-bsn-champagne transition-colors">
              <Heart className="w-4 h-4 text-bsn-gold" /> {es ? "Apoya la misión" : "Support the mission"}
            </Link>
            <Link to="/merch-preview" data-testid="merch-vault-preview-link"
              className="mt-4 flex items-center gap-1.5 text-xs text-bsn-ice/45 hover:text-bsn-gold transition-colors">
              <Lock className="w-3 h-3" /> {es ? "Bóveda de mercancía (muy pronto)" : "Patron Merch Vault (Coming Soon)"}
            </Link>
          </div>}
        </div>

        <div className="gold-rule my-10" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-bsn-ice/50">
          <p>© {new Date().getFullYear()} Bright Stories Network. {es ? "Alojado con cuidado en la nube de Hostinger." : "Hosted with care on Hostinger cloud."}</p>
          <p className="text-center sm:text-right">
            {es ? "No reemplaza la atención de emergencia. ¿En peligro inmediato? Llama al " : "Not a substitute for emergency care. In immediate danger? Call "}<strong className="text-bsn-champagne">911</strong> {es ? "o al" : "or"} <strong className="text-bsn-champagne">988</strong>.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
